import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowLeft } from "lucide-react";
import Logo from "@/components/site/Logo";

export default function AdminForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null); // { emailSent, message }

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            setResult({
                emailSent: !!data?.email_sent,
                message: data?.message || "Reset request received.",
            });
            if (data?.email_sent) {
                toast.success("Reset link sent — check your inbox");
            } else {
                toast.warning("Email delivery failed — see instructions on screen");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send reset link");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white grid lg:grid-cols-2">
            <div className="hidden lg:block relative bg-[#18181a] border-r border-white/10">
                <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: "url(https://images.pexels.com/photos/33814734/pexels-photo-33814734.jpeg?auto=compress&cs=tinysrgb&w=1200)" }} />
                <div className="absolute inset-0 bg-gradient-to-br from-black/70 to-black/90" />
                <div className="absolute top-0 left-0 w-full h-2 hazard-stripes opacity-80" />
                <div className="relative z-10 h-full flex flex-col justify-between p-12">
                    <Logo variant="compact" />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d52b1e] mb-2">Forgot Password</div>
                        <h1 className="font-display uppercase text-5xl leading-none">Reset<br />via email.</h1>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-md bg-[#18181a] border border-white/10 p-8" data-testid="forgot-password-form">
                    <Link to="/admin/login" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white mb-8">
                        <ArrowLeft className="w-3 h-3" /> Back to login
                    </Link>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="bg-[#d52b1e] text-white w-10 h-10 flex items-center justify-center">
                            <Mail className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Password reset</div>
                            <div className="font-display uppercase text-2xl">Forgot password</div>
                        </div>
                    </div>

                    {result ? (
                        <div className="text-sm text-zinc-300" data-testid="forgot-success-message">
                            {result.emailSent ? (
                                <>
                                    <div className="inline-block bg-emerald-700/20 border border-emerald-700 text-emerald-300 text-[10px] uppercase tracking-[0.3em] px-2 py-1 mb-4">Sent</div>
                                    <p className="mb-4">{result.message}</p>
                                </>
                            ) : (
                                <>
                                    <div className="inline-block bg-amber-700/20 border border-amber-700 text-amber-300 text-[10px] uppercase tracking-[0.3em] px-2 py-1 mb-4">Delivery failed</div>
                                    <p className="mb-4 whitespace-pre-line">{result.message}</p>
                                    <p className="text-xs text-zinc-500">
                                        Contact your Emergent platform operator with the request ID to retrieve the reset link from the backend logs.
                                    </p>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            <p className="text-sm text-zinc-400 mb-6">
                                Enter your admin email — we'll send you a reset link.
                            </p>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Admin email"
                                className="industrial-input"
                                required
                                data-testid="forgot-email-input"
                            />
                            <button
                                type="submit"
                                disabled={submitting}
                                className="mt-8 w-full bg-[#d52b1e] hover:bg-[#f03022] disabled:opacity-60 text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold"
                                data-testid="forgot-submit-btn"
                            >
                                {submitting ? "Sending..." : "Send reset link"}
                            </button>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
}
